import PropTypes from 'prop-types';
import React from 'react';
import { asyncConnect } from 'redux-connect';
import { get } from 'helpers/HelperFunctions';
import { Link } from 'react-router-dom';

import Breadcrumbs from 'components/Shared/Breadcrumbs';
import BriefDetail from 'components/BriefDetailsClient/BriefDetail/BriefDetail';
import BriefDetailsFreelancer from 'containers/BriefDetailsFreelancer/BriefDetailsFreelancer';
import Loader from 'components/Loader/Loader';
import NotFound from 'containers/NotFound/NotFound';
import PublicQuestionsList from 'components/BriefDetailsClient/PublicQuestionsList/PublicQuestionsList';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import UserSnippets from 'components/Shared/UserSnippets/UserSnippets';
import { trackEvent } from 'helpers/Analytics';

import {
  update as updateUser,
  updateLocal as updateLocalUser,
} from 'redux/modules/auth';

import {
  setSelectedRoleCategory,
  clearSelectedRoleCategory,
} from 'redux/modules/selectedRoleCategory';

import { answerQuestion as answerPublicQuestion } from 'redux/modules/publicQuestions2';

import {
  decrementTotalPendingQuestions,
  loadBrief,
  loadPendingPublicQuestions,
  loadPublicQuestions,
  updateBrief,
} from 'redux/modules/briefs2';

import styles from './BriefDetailsClient.module.scss';
import { getRoleCategory } from 'helpers/Constants';

@asyncConnect(
  [
    {
      promise: ({
        store: { dispatch, getState },
        match: {
          params: { id },
        },
      }) => {
        const promises = [];

        const encodedId = id ? id.split('-')[0] : null;

        if (encodedId && !get(getState(), ['briefs2', encodedId])) {
          promises.push(dispatch(loadBrief(encodedId)));
        }

        return Promise.all(promises);
      },
    },
  ],
  (state) => ({
    auth: state.auth,
    briefs: state.briefs2,
    entities: state.entities,
    publicQuestions: state.publicQuestions2,
  }),
  {
    answerPublicQuestion,
    decrementTotalPendingQuestions,
    loadBrief,
    loadPendingPublicQuestions,
    loadPublicQuestions,
    updateBrief,
    updateUser,
    updateLocalUser,
    setSelectedRoleCategory,
    clearSelectedRoleCategory,
  }
)
class BriefDetailsClient extends React.Component {
  static propTypes = {
    auth: PropTypes.object,
    briefs: PropTypes.object,
    entities: PropTypes.object,
    location: PropTypes.object,
    publicQuestions: PropTypes.object,
    answerPublicQuestion: PropTypes.func,
    decrementTotalPendingQuestions: PropTypes.func,
    loadBrief: PropTypes.func,
    loadPendingPublicQuestions: PropTypes.func,
    loadPublicQuestions: PropTypes.func,
    updateBrief: PropTypes.func,
    updateUser: PropTypes.func,
    updateLocalUser: PropTypes.func,
    match: PropTypes.object,
    history: PropTypes.object,
    setSelectedRoleCategory: PropTypes.func,
    clearSelectedRoleCategory: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      invitedFreelancers: get(
        this.getBrief(props),
        ['invites', 'invites'],
        []
      ).map((inviteEntityId) =>
        get(props.entities, [
          'users',
          get(props.entities, ['invites', inviteEntityId, 'user_id']),
        ])
      ),
    };
  }

  componentDidMount() {
    const {
      auth,
      briefs,
      match: { params },
      location,
      loadBrief,
      setSelectedRoleCategory,
      history,
    } = this.props;

    const brief = this.getBrief();

    // Track old URL visits and redirect owner clients to new ATS URL
    if (brief && auth.user && brief.user_id === auth.user.id) {
      if (!params.subsection) {
        const referrer = document.referrer || 'direct';
        const currentPath = window.location.pathname;
        trackEvent('old_ats_url_visited', {
          oldUrl: currentPath,
          referrer: referrer,
          briefId: this.getEncodedId(),
          userType: auth.user.type,
        });

        const newUrl = brief.links.ats_main_relative || `/ats/${params.id}`;
        history.replace(newUrl);
      }
    }

    if (params.subsection === 'review' && brief && auth.user) {
      trackEvent('user-lands-on-post-review', {
        role: brief.role,
        skill: brief.skill,
        loggedIn: !!auth.user,
        userType: auth.user ? auth.user.type : 'guest',
      });
    }

    if (!auth.user && location.query.utm_source === 'email') {
      const encodedId = this.getEncodedId();
      document.location = `/signin?referer=${encodeURIComponent(
        `/projects/${encodedId}`
      )}`;
    }

    const encodedId = this.getEncodedId();
    if (
      brief &&
      !get(briefs, [encodedId, 'loading'], false) &&
      !get(briefs, [encodedId, 'loaded'], false)
    ) {
      loadBrief(encodedId);
    }

    if (brief && brief.role) {
      setSelectedRoleCategory(getRoleCategory(brief.role));
    }

    this.initLoadPublicQuestions(true);
    this.initLoadPublicQuestions(false);
  }

  componentDidUpdate(prevProps) {
    this.initLoadPublicQuestions(true);
    this.initLoadPublicQuestions(false);

    const {
      auth,
      match: { params },
      history,
    } = this.props;

    const brief = this.getBrief();
    const prevBrief = this.getBrief(prevProps);

    // If the brief wasn't available on mount (still loading), the redirect in
    // componentDidMount won't have fired. Re-check here once the brief lands.
    if (
      brief &&
      !prevBrief &&
      auth.user &&
      brief.user_id === auth.user.id &&
      !params.subsection
    ) {
      const newUrl = brief.links.ats_main_relative || `/ats/${params.id}`;
      history.replace(newUrl);
    }
  }

  componentWillUnmount() {
    this.props.clearSelectedRoleCategory();
  }

  getEncodedId = (props = this.props) => {
    const { match } = props;
    const urlId = match?.params?.id;
    return urlId ? urlId.split('-')[0] : null;
  };

  getBrief = (props = this.props) => {
    const encodedId = this.getEncodedId(props);
    return get(
      props.entities,
      ['briefs', get(props.briefs, [encodedId, 'id'])],
      null
    );
  };

  handleAnswerQuestion(brief, question, answer) {
    trackEvent('client-brief-answers-public-question', {
      briefId: brief?.id,
      questionId: question?.id,
    });
    this.props.answerPublicQuestion(brief, question, answer);
    const encodedId = this.getEncodedId();
    this.props.decrementTotalPendingQuestions(encodedId);
  }

  handleUpdateBrief(brief, fields, tempAvatar, key = '') {
    if (fields.brief) {
      const encodedId = this.getEncodedId();
      this.props.updateBrief(encodedId, brief, fields.brief);
    }

    if (typeof fields.user !== 'undefined') {
      this.handleUpdateUser(fields.user, tempAvatar, key);
    }
  }

  handleUpdateUser(fields, tempAvatar, key) {
    this.props.updateUser(this.props.auth.user, fields, false, [], key);

    if (tempAvatar) {
      this.props.updateLocalUser(this.props.auth.user.id, {
        avatars: { temp: tempAvatar },
      });
    }
  }

  initLoadPublicQuestions = (pending = true) => {
    const { briefs } = this.props;
    const encodedId = this.getEncodedId();

    if (
      this.getBrief() &&
      !get(briefs, [encodedId, 'loadedPublicQuestions'], false) &&
      !get(briefs, [encodedId, 'loadingPublicQuestions'], false)
    ) {
      if (pending) {
        this.props.loadPendingPublicQuestions(encodedId);
      } else {
        this.props.loadPublicQuestions(encodedId);
      }
    }
  };

  render() {
    const {
      auth,
      briefs,
      entities,
      match: { params },
      publicQuestions,
    } = this.props;

    const encodedId = this.getEncodedId();
    const { invitedFreelancers } = this.state;
    const hasInvited = invitedFreelancers.length > 0;

    const brief = this.getBrief();
    const owner =
      brief && auth.user && brief.user_id === auth.user.id
        ? get(auth, ['user'], null)
        : brief
        ? get(entities, ['users', brief.user_id], null)
        : null;

    // if loaded but deleted - only show this if we're sure it's deleted and not still loading
    // Check if brief is fully loaded and not in a loading state before determining it's deleted
    const briefIsLoading = get(briefs, [encodedId, 'loading'], false);
    const briefIsFullyLoaded = get(briefs, [encodedId, 'loaded'], false);

    // if loading
    if (briefIsLoading) {
      return <Loader className={styles.loader} loading />;
    }

    // if loaded but not found
    if (briefIsFullyLoaded && !brief) {
      return <NotFound />;
    }

    // If this is the owner viewing the base URL (no subsection), show a loader
    // while componentDidMount handles the redirect to the new ATS URL
    if (
      brief &&
      auth.user &&
      brief.user_id === auth.user.id &&
      !params.subsection
    ) {
      return <Loader className={styles.loader} loading />;
    }

    if (
      briefIsFullyLoaded &&
      !briefIsLoading &&
      brief &&
      brief.deleted_at !== ''
    ) {
      return (
        <div className={`container ${styles.container}`}>
          {header}
          <Breadcrumbs className={styles.breadcrumbs}>
            <Link to="/ats">Manage Jobs</Link>
            Job Status
          </Breadcrumbs>

          <div className={styles.deletedBrief}>This job has been deleted</div>
        </div>
      );
    }

    const header = (
      <h1 className={styles.header}>
        <span className={styles.headerIcon}>
          <Link to={'/ats'}>
            <SvgIcon icon="chevron-left" size="24" />
          </Link>
        </span>
        {get(brief, ['text'])}
      </h1>
    );

    if (params.subsection === 'review' && brief) {
      if (!auth.user || !brief || auth.user.id !== brief.user_id) {
        return this.props.history.replace('/');
      }

      return (
        <div className={`container ${styles.container}`}>
          <h3 className={styles.header}>
            <SvgIcon icon="tick" /> Your job has been submitted to the Twine
            team for review before going live
          </h3>
          <div className="f f-row f-xs-col jc-center">
            <div className={`${hasInvited ? 'f-07' : ''} m`}>
              <BriefDetail
                isReview
                narrow={!hasInvited}
                brief={brief}
                browseURL={`${brief.links.ats_main_relative}`}
                owner={owner}
                editable={get(auth, ['user', 'id'], null) === owner.id} // TODO: Also check that a pitch has not been accepted
                isOwner={get(auth, ['user', 'id'], null) === owner.id}
                onSaveEdit={(edits, tempAvatar = null, key = '') =>
                  this.handleUpdateBrief(brief, edits, tempAvatar, key)
                }
                user={auth.user}
              />
            </div>
            {hasInvited && (
              <div className="f-03 f-col m">
                <UserSnippets
                  linksActive={false}
                  freelancers={invitedFreelancers}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (params.subsection === 'brief' && brief) {
      return (
        <div className={`container ${styles.container}`}>
          {header}
          <Breadcrumbs className={styles.breadcrumbs}>
            <Link to="/ats">Manage Jobs</Link>
            <Link
              to={brief.links.ats_main_relative || brief.links.main_relative}
            >
              Job Status
            </Link>
            Job Details
          </Breadcrumbs>
          <BriefDetail
            brief={brief}
            owner={owner}
            editable={get(auth, ['user', 'id'], null) === owner.id} // TODO: Also check that a pitch has not been accepted
            isOwner={get(auth, ['user', 'id'], null) === owner.id}
            onSaveEdit={(edits, tempAvatar = null, key = '') =>
              this.handleUpdateBrief(brief, edits, tempAvatar, key)
            }
            user={auth.user}
          />
        </div>
      );
    }

    if (params.subsection === 'questions' && brief) {
      return (
        <div className={`container ${styles.container}`}>
          {header}
          <Breadcrumbs className={styles.breadcrumbs}>
            <Link to="/ats">Manage Jobs</Link>
            <Link
              to={brief.links.ats_main_relative || brief.links.main_relative}
            >
              Job Status
            </Link>
            Public Questions
          </Breadcrumbs>
          <PublicQuestionsList
            more={get(briefs, [encodedId, 'morePublicQuestions'], false)}
            postingAnswers={Object.keys(publicQuestions).filter((questionId) =>
              get(publicQuestions, [questionId, 'answering'], false)
            )}
            loading={get(briefs, [encodedId, 'loadingPublicQuestions'], false)}
            onAnswer={(question, answer) =>
              this.handleAnswerQuestion(brief, question, answer)
            }
            onLoadMore={() =>
              this.props.loadPublicQuestions(
                encodedId,
                get(briefs, [encodedId, 'publicQuestionsPagesLoaded'], 0) + 1
              )
            }
            questions={get(briefs, [encodedId, 'publicQuestions'], []).map(
              (question) => get(entities, ['publicQuestions', question], false)
            )}
          />
        </div>
      );
    }

    return <BriefDetailsFreelancer {...this.props} encodedId={encodedId} />;
  }
}

export default BriefDetailsClient;
